/**
 * Field Rating Component
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import { formatFromVariableSelect, formatFromVariableText } from '../../format-utils'
import type { VariableSelectValue, VariableTextValue } from '../../types'
import { deepMerge, parseToVariableSelect, parseToVariableText } from '../../parser-utils'
import fieldRatingTemplate from './field-rating.template.json'

export interface IFieldRatingComponentSettings {
  // Data settings
  'field-rating-data-value': string // Column ID
  'field-rating-data-label': VariableTextValue
  'field-rating-data-default-value': VariableSelectValue
  'field-rating-data-default-value-editable': boolean
  // Behavior settings
  'field-rating-behavior-required'?: boolean
}

export interface IFieldRatingComponent extends IBuilderComponent {
  componentId: 'field-rating'
  groupId: 'form'
  settings: IFieldRatingComponentSettings
}

/**
 * Field Rating AI Input
 */
export interface AiFieldRatingInput {
  columnId: string // Required: maps to table column
  label?: string[] // Format: ["text:Rating"] or ["text:Rate ", "cr:itemName"]
  defaultValue?: number // Default value
  required?: boolean // Is this field required?
}

const FIELD_RATING_DEFAULT_TEMPLATE = fieldRatingTemplate as unknown as IFieldRatingComponentSettings

export class FieldRatingComp {
  getTemplate(): IFieldRatingComponentSettings {
    return FIELD_RATING_DEFAULT_TEMPLATE
  }

  /**
   * format field rating component settings to AI field rating input
   */
  format(settings: IFieldRatingComponentSettings): AiFieldRatingInput {
    const result: AiFieldRatingInput = {
      columnId: settings['field-rating-data-value'],
    }

    // Label
    const label = formatFromVariableText(settings['field-rating-data-label'])
    if (label && label.length > 0) {
      result.label = label
    }

    // Default Value (convert from VariableSelectValue to number)
    const defaultValue = formatFromVariableSelect(settings['field-rating-data-default-value'])
    if (defaultValue) {
      // Parse the defaultValue string (e.g., "text:5")
      if (defaultValue.startsWith('text:')) {
        const value = defaultValue.replace('text:', '')
        const numValue = parseInt(value, 10)
        if (!isNaN(numValue)) {
          result.defaultValue = numValue
        }
      }
    }

    // Required
    if (settings['field-rating-behavior-required'] !== undefined) {
      result.required = settings['field-rating-behavior-required']
    }

    return result
  }

  /**
   * parse AI field rating input to field rating component settings
   */
  parse(input: AiFieldRatingInput): IFieldRatingComponentSettings {
    const result = deepMerge(FIELD_RATING_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['field-rating-data-value'] = input.columnId
    }

    // Data - Label
    if (input.label) {
      result['field-rating-data-label'] = parseToVariableText(input.label)
    }

    // Data - Default Value
    if (input.defaultValue !== undefined) {
      result['field-rating-data-default-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: input.defaultValue.toString(),
      }
    }

    return result
  }
}

